/**
 * State section chart contract — Population Trends + Red List Categories.
 * Ids, colours, geometry — no magic strings in chart/chrome code.
 */

export const STATE_FLYWAY_FILTERS = [
  'all',
  'americas',
  'african-eurasian',
  'central-asia',
  'east-asia-australasia',
  'marine',
] as const;

export type StateFlywayFilterId = (typeof STATE_FLYWAY_FILTERS)[number];

/** Flyway accent colours (active filter tab + donut centre %). */
export const STATE_FLYWAY_COLORS = {
  americas: 'var(--flyway-americas)',
  'african-eurasian': 'var(--flyway-african-eurasian)',
  'central-asia': 'var(--flyway-central-asia)',
  'east-asia-australasia': 'var(--flyway-east-asia-australasia)',
  marine: 'var(--flyway-marine)',
} as const satisfies Record<Exclude<StateFlywayFilterId, 'all'>, string>;

export const STATE_VIEW_MODES = ['default', 'speciesGroup', 'habitat'] as const;

export type StateChartViewMode = (typeof STATE_VIEW_MODES)[number];

/** Prefetch margin before State chart islands start their dynamic import. */
export const STATE_CHART_VIEWPORT_ROOT_MARGIN = '200px 0px';

/** Default flyway filter — “Show all” / cleared selection. */
export const STATE_CHART_DEFAULT_FLYWAY_FILTER =
  'all' satisfies StateFlywayFilterId;

/** Default view mode — aggregate donut / cleared selection. */
export const STATE_CHART_DEFAULT_VIEW_MODE =
  'default' satisfies StateChartViewMode;

export const STATE_SPECIES_GROUPS = [
  'waterfowl',
  'seabirds',
  'shorebirds',
  'raptors',
  'passerines',
] as const;

export type StateSpeciesGroupId = (typeof STATE_SPECIES_GROUPS)[number];

export const STATE_HABITATS = [
  'forest',
  'shrubland',
  'grassland',
  'inland-wetland',
  'marine-oceanic',
  'coastal-wetland',
  'artificial-terrestrial',
] as const;

export type StateHabitatId = (typeof STATE_HABITATS)[number];

export const POPULATION_TREND_IDS = [
  'decreasing',
  'stable',
  'increasing',
  'unknown',
] as const;

export type PopulationTrendId = (typeof POPULATION_TREND_IDS)[number];

/** CSS custom-property colours for population trend segments. */
export const POPULATION_TREND_COLORS = {
  decreasing: 'var(--color-red)',
  stable: 'var(--color-orange)',
  increasing: 'var(--color-green)',
  unknown: 'var(--color-grey-2)',
} as const satisfies Record<PopulationTrendId, string>;

export const POPULATION_TRENDS_HERO_IDS = ['decreasing'] as const satisfies readonly PopulationTrendId[];

export const IUCN_CATEGORY_IDS = [
  'extinct',
  'critically-endangered',
  'endangered',
  'vulnerable',
  'near-threatened',
  'least-concern',
  'data-deficient',
] as const;

export type IucnCategoryId = (typeof IUCN_CATEGORY_IDS)[number];

/** CSS custom-property colours for IUCN category segments. */
export const IUCN_CATEGORY_COLORS = {
  extinct: 'var(--iucn-extinct)',
  'critically-endangered': 'var(--iucn-critically-endangered)',
  endangered: 'var(--iucn-endangered)',
  vulnerable: 'var(--iucn-vulnerable)',
  'near-threatened': 'var(--iucn-near-threatened)',
  'least-concern': 'var(--iucn-least-concern)',
  'data-deficient': 'var(--iucn-grey)',
} as const satisfies Record<IucnCategoryId, string>;

/**
 * Border for dark legend swatches (Extinct `#000`) on dark chart surfaces.
 * `#828282` — `--color-grey-2`.
 */
export const CHART_LEGEND_OUTLINED_SWATCH_BORDER = 'var(--color-grey-2)';

/**
 * Record field keys for IUCN percentages (camelCase in data files).
 * Order matches IUCN_CATEGORY_IDS.
 */
export const IUCN_RECORD_VALUE_KEYS = [
  'extinct',
  'criticallyEndangered',
  'endangered',
  'vulnerable',
  'nearThreatened',
  'leastConcern',
  'dataDeficient',
] as const;

export type IucnRecordValueKey = (typeof IUCN_RECORD_VALUE_KEYS)[number];

export const IUCN_CATEGORY_TO_RECORD_KEY = {
  extinct: 'extinct',
  'critically-endangered': 'criticallyEndangered',
  endangered: 'endangered',
  vulnerable: 'vulnerable',
  'near-threatened': 'nearThreatened',
  'least-concern': 'leastConcern',
  'data-deficient': 'dataDeficient',
} as const satisfies Record<IucnCategoryId, IucnRecordValueKey>;

/** Globally threatened donut segments — CR + EN + VU extend the outer radius. */
export const RED_LIST_HERO_IDS = [
  'critically-endangered',
  'endangered',
  'vulnerable',
] as const satisfies readonly IucnCategoryId[];

export const RED_LIST_THREATENED_IDS = RED_LIST_HERO_IDS;

/**
 * Stacked-bar track height — mobile (UI Mobile bar rows ≈ 78px).
 * Desktop overrides via `BAR_TRACK_HEIGHT_DESKTOP_REM` + matchMedia.
 */
export const BAR_TRACK_HEIGHT_MOBILE_REM = 4.875;

/**
 * Stacked-bar track height — desktop (Figma Bar chart rows ≈ 120px).
 * Five species rows + 12px gaps fill the 648px plate.
 */
export const BAR_TRACK_HEIGHT_DESKTOP_REM = 7.5;

/**
 * Max share of the chart plate each stacked-bar row may occupy.
 * Caps sparse views (e.g. Marine → one seabirds bar) so a single row
 * does not stretch to full plate height when a definite plate height exists.
 */
export const BAR_TRACK_MAX_HEIGHT_RATIO = 0.2;

/**
 * Vertical gap between stacked-bar rows.
 * Figma desktop + mobile: 12px between Bar chart frames.
 */
export const BAR_ROW_GAP_REM = 0.75;

/**
 * Below Tailwind `sm`, the row label stacks above the track. Motion locks
 * row min/max height — that height must include the label + gap or the track
 * is clipped (`overflow: hidden`) and in-bar % labels look too low.
 * Keep in sync with `.row` gap + `--type-label-s` metrics in CSS.
 * Side-by-side labels may wrap to `BAR_ROW_LABEL_MAX_LINES`; stacked layout
 * reserves up to that many lines of `--type-label-s`.
 */
export const BAR_ROW_LABEL_MAX_LINES = 2;
/** One line of Label S (0.75rem × 1.2 line-height). */
export const BAR_ROW_LABEL_LINE_REM = 0.9;
export const BAR_ROW_STACKED_LABEL_REM =
  BAR_ROW_LABEL_LINE_REM * BAR_ROW_LABEL_MAX_LINES;
export const BAR_ROW_STACKED_GAP_REM = 0.35;

/**
 * Side-by-side (sm+) row label column + gap before the track.
 * Figma Bar chart `565:99047`: Label `w-[100px]` `justify-end` / `text-right`,
 * flex `gap-[32px]` to the bar.
 */
export const BAR_ROW_LABEL_COLUMN_REM = 6.25; /* 100px */
export const BAR_ROW_LABEL_TO_TRACK_GAP_REM = 2; /* 32px — `--space-4` */

/**
 * Tailwind `sm` (40rem) — row label moves beside the track; row height = track.
 * Matches CSS Modules `theme(--breakpoint-sm)`.
 */
export const BAR_ROW_SIDE_BY_SIDE_MEDIA = '(width >= 40rem)' as const;

/**
 * Tailwind `md` (48rem) — desktop stacked-bar track height kicks in here.
 * Matches CSS Modules `theme(--breakpoint-md)`.
 */
export const BAR_TRACK_DESKTOP_MEDIA = '(width >= 48rem)' as const;

/**
 * Horizontal padding (px) required on each side of an in-bar % label.
 * If the segment cannot fit the label + this padding, hide the label and
 * show the hover tooltip instead (Feedbucket / Figma contract).
 */
export const BAR_LABEL_PADDING_INLINE_PX = 8;

/** Hero donut arcs extend outer radius by this fraction of the base radius. */
export const DONUT_HERO_RADIUS_BOOST = 0.12;

/**
 * Max outer radius (hero) as a fraction of half the SVG box.
 * Figma: 620px hero in 648px plate ≈ 0.956; use slightly less for a clear
 * inset so the hero bulge never reads as clipped at the top edge.
 */
export const DONUT_MAX_OUTER_RADIUS_RATIO = 0.93;

/**
 * Inner hole as a fraction of the layout radius.
 * Figma Diagram `796:100198` Ellipse 17: 236px hole in 648px plate → 118/324 ≈ 0.36.
 * Mobile `971:54238` uses a square plate + scaled centre type so this ratio
 * still reads with enough breathing room when the chart is larger.
 */
export const DONUT_INNER_RADIUS_RATIO = 0.36;

/** Gap between pie slices (radians). Keep small — Figma arcs nearly abut. */
export const DONUT_PAD_ANGLE = 0.008;

/**
 * Pie start angle in radians. d3 uses 0 = 12 o'clock, clockwise positive.
 * 3π/2 = 270° = 9 o'clock — matches Figma Population Trends / Red List donuts.
 */
export const DONUT_START_ANGLE = (3 * Math.PI) / 2;

/** Must be start + 2π — visx/d3 default endAngle is absolute 2π, not start+τ. */
export const DONUT_END_ANGLE = DONUT_START_ANGLE + 2 * Math.PI;

/**
 * Focus / keyboard tooltip sits this far between inner and outer radius
 * (0 = hole edge, 1 = outer rim). Keeps the pill in the coloured band.
 */
export const DONUT_TOOLTIP_RADIUS_T = 0.62;

/**
 * Pointer tooltips closer than this fraction of the layout radius from the
 * chart centre are pushed outward so the pill never covers the centre stack.
 */
export const DONUT_TOOLTIP_MIN_RADIUS_RATIO = 0.48;

/**
 * Mobile donut plate — Figma Population Trends `971:54238` is square 357×357
 * (hero fills the plate). Desktop keeps the wider diagram frame below.
 */
export const STATE_CHART_ASPECT_RATIO_MOBILE = 1;

/** Desktop reserved plate aspect ratio — Figma diagram 723×648. */
export const STATE_CHART_ASPECT_RATIO = 723 / 648;

/**
 * Tailwind `md` (48rem) — desktop donut plate aspect + centre type kick in.
 * Matches CSS Modules `theme(--breakpoint-md)`.
 */
export const STATE_CHART_DESKTOP_MEDIA = '(width >= 48rem)' as const;

export const STATE_CHART_MAX_WIDTH_REM = 45.1875;

/** Centre stack — Figma mobile Frame 37526 `971:54211` ≈ 72.6×54. */
export const DONUT_CENTER_STACK_WIDTH_MOBILE_REM = 4.5375;
export const DONUT_CENTER_VALUE_SIZE_MOBILE_REM = 1.75; /* 28px */
export const DONUT_CENTER_LABEL_SIZE_MOBILE_REM = 0.75; /* 12px */

/** Centre stack — Figma desktop Frame 37526 `796:100208` 126×94. */
export const DONUT_CENTER_STACK_WIDTH_DESKTOP_REM = 7.875;
export const DONUT_CENTER_STACK_HEIGHT_DESKTOP_REM = 5.875;
export const DONUT_CENTER_VALUE_SIZE_DESKTOP_REM = 3; /* 48px */
export const DONUT_CENTER_LABEL_SIZE_DESKTOP_REM = 1.25; /* 20px */

/** Flyway ids that carry RLI series (excludes the aggregate All flyways view). */
export const RED_LIST_INDEX_FLYWAYS = [
  'americas',
  'african-eurasian',
  'central-asia',
  'east-asia-australasia',
  'marine',
] as const satisfies ReadonlyArray<Exclude<StateFlywayFilterId, 'all'>>;

export type RedListIndexFlywayId = (typeof RED_LIST_INDEX_FLYWAYS)[number];

/** Aggregate series plus each named flyway: keys present in the RLI dataset. */
export const RED_LIST_INDEX_SERIES_IDS = ['all', ...RED_LIST_INDEX_FLYWAYS] as const;

export type RedListIndexSeriesId = (typeof RED_LIST_INDEX_SERIES_IDS)[number];

/** Separate UI filter for comparing every named flyway series. */
export const RED_LIST_INDEX_COMPARISON_FILTER_ID = 'show-all' as const;

/** Filter tabs shown on the Red List Index chart. */
export const RED_LIST_INDEX_FILTERS = [
  ...RED_LIST_INDEX_SERIES_IDS,
  RED_LIST_INDEX_COMPARISON_FILTER_ID,
] as const;

export type RedListIndexFilterId = (typeof RED_LIST_INDEX_FILTERS)[number];

/**
 * Continuous X domain for the RLI line — full year span of the dataset
 * (includes 1988–1989 points left of the first tick).
 */
export const RED_LIST_INDEX_YEAR_DOMAIN = [1988, 2025] as const;

/** X-axis tick labels — every 5 years from 1990. */
export const RED_LIST_INDEX_X_TICK_YEARS = [
  1990, 1995, 2000, 2005, 2010, 2015, 2020, 2025,
] as const;

/** Reserved plate aspect — Figma graph 723×469. */
export const RED_LIST_INDEX_ASPECT_RATIO = 723 / 469;

/** @see `RED_LIST_INDEX_MAX_WIDTH_REM` / `_2XL` in `src/constants/layout.ts`. */
export {
  RED_LIST_INDEX_MAX_WIDTH_REM,
  RED_LIST_INDEX_MAX_WIDTH_2XL_REM,
} from '@/constants/layout';

/**
 * SVG plot margins. Compact (below `lg`) drops the rotated risk-label column —
 * Figma mobile (`969:53618`) places those captions above/below the plot.
 */
export const RED_LIST_INDEX_MARGIN = {
  top: 18,
  right: 16,
  bottom: 40,
  left: 100,
} as const;

/** Y-tick gutter only — Figma mobile graph ~47px left of the plot. */
export const RED_LIST_INDEX_MARGIN_COMPACT = {
  top: 12,
  right: 12,
  bottom: 36,
  left: 48,
} as const;

/**
 * Y-axis domain expanded to fit imported terrestrial series (~0.925–0.966).
 * Higher index = lower extinction risk.
 */
export const RED_LIST_INDEX_Y_DOMAIN = [0.92, 0.97] as const;

export const RED_LIST_INDEX_Y_TICKS = [
  0.97, 0.96, 0.95, 0.94, 0.93, 0.92,
] as const;

/**
 * Show all spans both terrestrial (~0.93–0.96) and Marine (~0.75) series.
 * Confidence intervals are intentionally omitted from the comparison view.
 */
export const RED_LIST_INDEX_COMPARISON_Y_DOMAIN = [0.74, 0.97] as const;

export const RED_LIST_INDEX_COMPARISON_Y_TICKS = [0.95, 0.9, 0.85, 0.8, 0.75] as const;

/**
 * Marine RLI sits far below terrestrial flyways (~0.75). Used when the Marine
 * filter is active so the line + CI band fit with even 0.01 tick steps and
 * enough headroom above the confidence interval.
 */
export const RED_LIST_INDEX_MARINE_Y_DOMAIN = [0.74, 0.79] as const;

export const RED_LIST_INDEX_MARINE_Y_TICKS = [
  0.79, 0.78, 0.77, 0.76, 0.75, 0.74,
] as const;

/** Line + confidence band colours (Figma Key swatches). */
export const RED_LIST_INDEX_LINE_COLOR = 'var(--color-red)';
export const RED_LIST_INDEX_CI_COLOR = 'color-mix(in srgb, var(--color-red) 20%, transparent)';

/** Axis + extinction-risk annotation colour — Figma `#BDBDBD`. */
export const RED_LIST_INDEX_AXIS_LABEL_COLOR = '#BDBDBD';

/** Side-stat figure colours (Figma Graph Stat). */
export const RED_LIST_INDEX_IMPROVED_COLOR = 'var(--iucn-least-concern)';
export const RED_LIST_INDEX_DETERIORATED_COLOR = 'var(--color-red)';

/**
 * Fallback min segment value (%) when width measurement is unavailable
 * (SSR / first paint). Pixel fit with `BAR_LABEL_PADDING_INLINE_PX` wins
 * once the segment has a layout width.
 */
export const BAR_LABEL_MIN_VALUE = 4;

/**
 * In-bar label ink colours — Figma Bar chart tinted numerals (not inverse
 * white). Keys are segment ids for Population Trends + IUCN categories.
 */
export const BAR_LABEL_COLORS = {
  extinct: '#faf7f2',
  'critically-endangered': '#7b0000',
  endangered: '#674300',
  vulnerable: '#674300',
  'near-threatened': '#285b09',
  'least-concern': '#285b09',
  'data-deficient': '#333333',
  decreasing: '#7b0000',
  stable: '#674300',
  increasing: '#285b09',
  unknown: '#333333',
} as const satisfies Record<string, string>;
